import { z } from '@core/validation/validation.schema'
import type { FreshnessScope } from '@core/infra/freshness/freshness.config'

/**
 * What a `load()` may fall back to reading from `localStorage` when the network
 * is gone. One scope per cacheable list, keyed the same way `FreshnessScopes`
 * is and for the same reason: several Repositories need identical machinery, so
 * it is one keyed mechanism rather than a field per feature.
 *
 * `satisfies readonly FreshnessScope[]` makes "every cache scope is also a
 * freshness scope" a compile error rather than a guard test — a cache scope
 * that isn't route-preloaded and freshness-bounded has nothing to cache.
 * `admin.*` and `suggestions` are deliberately absent: privileged audit
 * data does not belong in `localStorage`, and a typeahead cache is not worth
 * persisting.
 */
export const OfflineCacheDataScopes = ['books', 'games'] as const satisfies readonly FreshnessScope[]

/**
 * `identity` is not a `loadIfNeeded` scope — it is `GET /me`, loaded once at
 * boot — but it must be cached too: without it, an offline boot leaves
 * `UserModel.capabilities` empty and every capability-gated affordance renders
 * as absent (see the class comment on OfflineCacheRepository for the
 * hand-editing caveat this creates).
 */
export const OfflineCacheScopes = [...OfflineCacheDataScopes, 'identity'] as const
export type OfflineCacheScope = (typeof OfflineCacheScopes)[number]

export const OFFLINE_CACHE_KEY_PREFIX = 'offline-cache:'

/**
 * Skip a write above this size rather than evict several good entries to fit
 * one oversized one — a miss is strictly better. A 500-row list DTO is ~200 KB;
 * this leaves headroom without inviting a single scope to dominate the budget.
 */
export const MAX_ENTRY_BYTES = 256 * 1024

/** A cached entry older than this is pruned at boot — a bound on how stale "last-seen" can get. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * The storage boundary's defined copy (CI-10): a stored blob is re-validated to
 * this shape before its `payload` is handed to the caller's DTO schema. `v`
 * lets a future format change invalidate old entries by bumping the literal.
 */
export const CacheEnvelopeSchema = z.object({
  v: z.literal(1),
  appVersion: z.string(),
  cachedAtMs: z.number(),
  scope: z.string(),
  payload: z.unknown(),
})
